import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { changeRequestsAPI, projectsAPI, employeesAPI, tasksAPI } from '../services/api';

// ── constants ────────────────────────────────────────────────────────────────
const STATUSES  = ['Draft', 'Submitted', 'Under Review', 'Approved', 'Rejected', 'Implemented'];
const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];
const TYPES     = ['Scope', 'Timeline', 'Resource', 'Cost', 'Risk'];

const STATUS_BADGE = {
  Draft:          'badge-info',
  Submitted:      'badge-primary',
  'Under Review': 'badge-warning',
  Approved:       'badge-success',
  Rejected:       'badge-danger',
  Implemented:    'badge-success',
};
const PRIORITY_BADGE = {
  Critical: 'badge-danger',
  High:     'badge-danger',
  Medium:   'badge-warning',
  Low:      'badge-info',
};

const emptyForm = () => ({
  title: '', project_id: '', raised_by: '', assigned_to: '',
  type_scope: false, type_timeline: false, type_resource: false, type_cost: false, type_risk: false,
  priority: 'Medium', status: 'Draft',
  date_raised: new Date().toISOString().split('T')[0],
  target_date: '', planned_hours: '', description: '', impact: '',
  task_ids: [],
});

const exportToExcel = (rows) => {
  const data = rows.map(r => ({
    'CR Ref':         r.cr_ref,
    'Title':          r.title,
    'Project':        r.project_name || '',
    'Raised By':      r.raised_by_name || '',
    'Assigned To':    r.assigned_to_name || '',
    'Types':          (r.types || []).join(', '),
    'Priority':       r.priority,
    'Status':         r.status,
    'Date Raised':    r.date_raised || '',
    'Target Date':    r.target_date || '',
    'Planned Hours':  r.planned_hours || '',
    'Description':    r.description || '',
    'Impact':         r.impact || '',
  }));

  const ws = XLSX.utils.json_to_sheet(data);
  ws['!cols'] = [
    {wch:10},{wch:35},{wch:25},{wch:20},{wch:20},
    {wch:30},{wch:12},{wch:16},{wch:14},{wch:14},
    {wch:14},{wch:40},{wch:40},
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Change Requests');
  XLSX.writeFile(wb, `Change_Requests_${new Date().toISOString().split('T')[0]}.xlsx`);
};

// ── component ────────────────────────────────────────────────────────────────
const ChangeRequests = () => {
  const [crs, setCrs]               = useState([]);
  const [loading, setLoading]       = useState(true);
  const [projects, setProjects]     = useState([]);
  const [employees, setEmployees]   = useState([]);
  const [projectTasks, setProjectTasks] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    search: '', project_id: '', status: '', priority: '', type: '', assigned_to: '', raised_by: '',
  });

  // Modal
  const [showModal, setShowModal]   = useState(false);
  const [editingId, setEditingId]   = useState(null);
  const [form, setForm]             = useState(emptyForm());
  const [saving, setSaving]         = useState(false);
  const [formError, setFormError]   = useState('');

  // Detail drawer
  const [drawer, setDrawer]         = useState(null); // full CR object

  // ── data loading ────────────────────────────────────────────────────────────
  const loadCRs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await changeRequestsAPI.getAll(params);
      setCrs(res.data);
    } catch (err) {
      console.error('Error loading CRs:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadCRs(); }, [loadCRs]);

  useEffect(() => {
    Promise.all([projectsAPI.getAll(), employeesAPI.getAll()]).then(([pRes, eRes]) => {
      setProjects(pRes.data);
      setEmployees(eRes.data);
    });
  }, []);

  // Load tasks when project selected in form
  useEffect(() => {
    if (form.project_id) {
      tasksAPI.getAllByProject(form.project_id).then(r => setProjectTasks(r.data)).catch(() => setProjectTasks([]));
    } else {
      setProjectTasks([]);
    }
  }, [form.project_id]);

  // ── form helpers ────────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError('');
    setShowModal(true);
  };

  const openEdit = async (cr) => {
    setEditingId(cr.cr_id);
    setFormError('');
    // Load linked tasks for this CR
    let linkedTaskIds = [];
    try {
      const res = await changeRequestsAPI.getById(cr.cr_id);
      linkedTaskIds = (res.data.linked_tasks || []).map(t => t.task_id);
    } catch {}
    setForm({
      title:          cr.title,
      project_id:     cr.project_id,
      raised_by:      cr.raised_by   || '',
      assigned_to:    cr.assigned_to || '',
      type_scope:     !!cr.type_scope,
      type_timeline:  !!cr.type_timeline,
      type_resource:  !!cr.type_resource,
      type_cost:      !!cr.type_cost,
      type_risk:      !!cr.type_risk,
      priority:       cr.priority,
      status:         cr.status,
      date_raised:    cr.date_raised || '',
      target_date:    cr.target_date || '',
      planned_hours:  cr.planned_hours || '',
      description:    cr.description || '',
      impact:         cr.impact || '',
      task_ids:       linkedTaskIds,
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const toggleTaskLink = (taskId) => {
    setForm(p => ({
      ...p,
      task_ids: p.task_ids.includes(taskId)
        ? p.task_ids.filter(id => id !== taskId)
        : [...p.task_ids, taskId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.title.trim())   { setFormError('Title is required.'); return; }
    if (!form.project_id)     { setFormError('Project is required.'); return; }
    if (!form.date_raised)    { setFormError('Date Raised is required.'); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        raised_by:   form.raised_by   || null,
        assigned_to: form.assigned_to || null,
        planned_hours: form.planned_hours ? parseFloat(form.planned_hours) : null,
      };
      if (editingId) {
        await changeRequestsAPI.update(editingId, payload);
        await changeRequestsAPI.setLinkedTasks(editingId, form.task_ids);
      } else {
        await changeRequestsAPI.create(payload);
      }
      setShowModal(false);
      loadCRs();
    } catch (err) {
      setFormError('Error saving. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, ref) => {
    if (!window.confirm(`Delete ${ref}?`)) return;
    try {
      await changeRequestsAPI.delete(id);
      loadCRs();
    } catch (err) {
      alert('Error deleting change request.');
    }
  };

  const openDrawer = async (cr) => {
    try {
      const res = await changeRequestsAPI.getById(cr.cr_id);
      setDrawer(res.data);
    } catch {}
  };

  const setFilter = (key, val) => setFilters(p => ({ ...p, [key]: val }));
  const clearFilters = () => setFilters({ search: '', project_id: '', status: '', priority: '', type: '', assigned_to: '', raised_by: '' });
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <div>
          <h2 className="page-title">Change Requests</h2>
          <p className="page-subtitle">Track and manage project change requests</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => exportToExcel(crs)}
            disabled={crs.length === 0}
          >
            📥 Export Excel
          </button>
          <button className="btn btn-primary" onClick={openCreate}>
            ＋ New Change Request
          </button>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {/* Search */}
          <div style={{ flex: '2', minWidth: '200px' }}>
            <label className="form-label">Search</label>
            <input
              className="form-control"
              placeholder="CR ref, title, description…"
              value={filters.search}
              onChange={e => setFilter('search', e.target.value)}
            />
          </div>
          {/* Project */}
          <div style={{ flex: '1', minWidth: '160px' }}>
            <label className="form-label">Project</label>
            <select className="form-control" value={filters.project_id} onChange={e => setFilter('project_id', e.target.value)}>
              <option value="">All Projects</option>
              {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.project_name}</option>)}
            </select>
          </div>
          {/* Status */}
          <div style={{ flex: '1', minWidth: '140px' }}>
            <label className="form-label">Status</label>
            <select className="form-control" value={filters.status} onChange={e => setFilter('status', e.target.value)}>
              <option value="">All Statuses</option>
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          {/* Priority */}
          <div style={{ flex: '1', minWidth: '130px' }}>
            <label className="form-label">Priority</label>
            <select className="form-control" value={filters.priority} onChange={e => setFilter('priority', e.target.value)}>
              <option value="">All Priorities</option>
              {PRIORITIES.map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
          {/* Type */}
          <div style={{ flex: '1', minWidth: '130px' }}>
            <label className="form-label">Type</label>
            <select className="form-control" value={filters.type} onChange={e => setFilter('type', e.target.value)}>
              <option value="">All Types</option>
              {TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          {/* Assigned To */}
          <div style={{ flex: '1', minWidth: '150px' }}>
            <label className="form-label">Assigned To</label>
            <select className="form-control" value={filters.assigned_to} onChange={e => setFilter('assigned_to', e.target.value)}>
              <option value="">All</option>
              {employees.map(e => <option key={e.employee_id} value={e.employee_id}>{e.employee_name}</option>)}
            </select>
          </div>
          {/* Clear */}
          {activeFilterCount > 0 && (
            <button className="btn btn-secondary btn-sm" onClick={clearFilters} style={{ marginBottom: '2px' }}>
              ✕ Clear ({activeFilterCount})
            </button>
          )}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            Change Requests
            {!loading && <span style={{ fontSize: '14px', fontWeight: '400', color: '#7f8c8d', marginLeft: '10px' }}>({crs.length})</span>}
          </h3>
        </div>
        {loading ? (
          <div className="loading">Loading…</div>
        ) : crs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🔄</div>
            <div className="empty-state-text">No change requests found</div>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>CR Ref</th>
                  <th>Title</th>
                  <th>Project</th>
                  <th>Raised By</th>
                  <th>Assigned To</th>
                  <th>Types</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Date Raised</th>
                  <th>Target Date</th>
                  <th>Planned Hrs</th>
                  <th style={{ minWidth: '150px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {crs.map(cr => (
                  <tr key={cr.cr_id}>
                    <td>
                      <button
                        onClick={() => openDrawer(cr)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3498db', fontWeight: '700', fontSize: '13px', padding: 0 }}
                      >
                        {cr.cr_ref}
                      </button>
                    </td>
                    <td style={{ maxWidth: '220px' }}>
                      <div style={{ fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cr.title}</div>
                    </td>
                    <td>
                      {cr.project_id
                        ? <Link to={`/projects/${cr.project_id}`}>{cr.project_name}</Link>
                        : '—'}
                    </td>
                    <td style={{ fontSize: '13px', color: '#495057' }}>{cr.raised_by_name || '—'}</td>
                    <td style={{ fontSize: '13px', color: '#495057' }}>{cr.assigned_to_name || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {(cr.types || []).map(t => (
                          <span key={t} style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '20px', background: '#e0e7ff', color: '#3730a3', fontWeight: '600' }}>{t}</span>
                        ))}
                      </div>
                    </td>
                    <td><span className={`badge ${PRIORITY_BADGE[cr.priority] || 'badge-info'}`}>{cr.priority}</span></td>
                    <td><span className={`badge ${STATUS_BADGE[cr.status] || 'badge-info'}`}>{cr.status}</span></td>
                    <td style={{ fontSize: '13px', whiteSpace: 'nowrap', color: '#64748b' }}>{cr.date_raised || '—'}</td>
                    <td style={{ fontSize: '13px', whiteSpace: 'nowrap', color: '#64748b' }}>{cr.target_date || '—'}</td>
                    <td style={{ fontSize: '13px', fontWeight: '600', color: cr.planned_hours ? '#5b21b6' : '#94a3b8' }}>
                      {cr.planned_hours ? `${cr.planned_hours}h` : '—'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-sm btn-primary" onClick={() => openEdit(cr)} style={{ marginRight: '6px' }}>✏️ Edit</button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(cr.cr_id, cr.cr_ref)}>🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Create / Edit Modal ── */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" style={{ maxWidth: '720px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingId ? 'Edit Change Request' : 'New Change Request'}</h3>
            </div>
            <form onSubmit={handleSubmit}>

              {/* Title */}
              <div className="form-group">
                <label className="form-label">Title *</label>
                <input className="form-control" name="title" value={form.title} onChange={handleChange} required placeholder="Brief summary of the change" />
              </div>

              {/* Project + Status */}
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Project *</label>
                  <select className="form-control" name="project_id" value={form.project_id} onChange={handleChange} required>
                    <option value="">Select Project</option>
                    {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.project_name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-control" name="status" value={form.status} onChange={handleChange}>
                    {STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              {/* Raised By + Assigned To */}
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Raised By</label>
                  <select className="form-control" name="raised_by" value={form.raised_by} onChange={handleChange}>
                    <option value="">Select Employee</option>
                    {employees.map(e => <option key={e.employee_id} value={e.employee_id}>{e.employee_name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Assigned To</label>
                  <select className="form-control" name="assigned_to" value={form.assigned_to} onChange={handleChange}>
                    <option value="">Select Employee</option>
                    {employees.map(e => <option key={e.employee_id} value={e.employee_id}>{e.employee_name}</option>)}
                  </select>
                </div>
              </div>

              {/* Types */}
              <div className="form-group">
                <label className="form-label">Impact Types</label>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', padding: '10px 14px', border: '2px solid #e1e8ed', borderRadius: '8px', background: '#fafafa' }}>
                  {TYPES.map(t => {
                    const key = `type_${t.toLowerCase()}`;
                    return (
                      <label key={t} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: form[key] ? '#3730a3' : '#64748b' }}>
                        <input type="checkbox" name={key} checked={!!form[key]} onChange={handleChange} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                        {t}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Priority + Planned Hours */}
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select className="form-control" name="priority" value={form.priority} onChange={handleChange}>
                    {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Planned Hours</label>
                  <input type="number" min="0" step="0.5" className="form-control" name="planned_hours" value={form.planned_hours} onChange={handleChange} placeholder="e.g. 16" />
                </div>
              </div>

              {/* Date Raised + Target Date */}
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Date Raised *</label>
                  <input type="date" className="form-control" name="date_raised" value={form.date_raised} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Target Date</label>
                  <input type="date" className="form-control" name="target_date" value={form.target_date} onChange={handleChange} />
                </div>
              </div>

              {/* Description */}
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-control" name="description" rows="3" value={form.description} onChange={handleChange} placeholder="Describe the change in detail…" />
              </div>

              {/* Impact */}
              <div className="form-group">
                <label className="form-label">Impact</label>
                <textarea className="form-control" name="impact" rows="3" value={form.impact} onChange={handleChange} placeholder="What is affected — scope, cost, timeline, resources…" />
              </div>

              {/* Linked Tasks */}
              {projectTasks.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Linked Tasks</label>
                  <div style={{ maxHeight: '160px', overflowY: 'auto', border: '2px solid #e1e8ed', borderRadius: '8px', padding: '8px 12px', background: '#fafafa' }}>
                    {projectTasks.map(t => (
                      <label key={t.task_id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 0', cursor: 'pointer', fontSize: '13px' }}>
                        <input
                          type="checkbox"
                          checked={form.task_ids.includes(t.task_id)}
                          onChange={() => toggleTaskLink(t.task_id)}
                          style={{ width: '15px', height: '15px', cursor: 'pointer' }}
                        />
                        <span style={{ fontWeight: '500', color: '#1e293b' }}>{t.task_name}</span>
                        {t.status_name && <span style={{ fontSize: '11px', color: '#94a3b8' }}>· {t.status_name}</span>}
                      </label>
                    ))}
                  </div>
                  {form.task_ids.length > 0 && (
                    <small style={{ color: '#5b21b6', marginTop: '4px', display: 'block' }}>{form.task_ids.length} task{form.task_ids.length !== 1 ? 's' : ''} linked</small>
                  )}
                </div>
              )}

              {formError && (
                <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', color: '#991b1b', fontSize: '14px', marginBottom: '8px' }}>
                  ❌ {formError}
                </div>
              )}

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : editingId ? '💾 Update' : '➕ Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Detail Drawer ── */}
      {drawer && (
        <>
          <div onClick={() => setDrawer(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 200, backdropFilter: 'blur(2px)' }} />
          <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '480px', maxWidth: '95vw', background: '#fff', zIndex: 201, boxShadow: '-8px 0 40px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>

            {/* Drawer header */}
            <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
              <div>
                <div style={{ fontWeight: '800', fontSize: '18px', color: '#1e293b' }}>{drawer.cr_ref}</div>
                <div style={{ fontSize: '14px', color: '#64748b', marginTop: '2px' }}>{drawer.title}</div>
              </div>
              <button onClick={() => setDrawer(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
            </div>

            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {/* Badges row */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <span className={`badge ${STATUS_BADGE[drawer.status] || 'badge-info'}`}>{drawer.status}</span>
                <span className={`badge ${PRIORITY_BADGE[drawer.priority] || 'badge-info'}`}>{drawer.priority}</span>
                {(drawer.types || []).map(t => (
                  <span key={t} style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '20px', background: '#e0e7ff', color: '#3730a3', fontWeight: '600' }}>{t}</span>
                ))}
              </div>

              {/* Meta grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {[
                  { label: 'Project',      value: drawer.project_name ? <Link to={`/projects/${drawer.project_id}`}>{drawer.project_name}</Link> : '—' },
                  { label: 'Assigned To',  value: drawer.assigned_to_name || '—' },
                  { label: 'Raised By',    value: drawer.raised_by_name || '—' },
                  { label: 'Date Raised',  value: drawer.date_raised || '—' },
                  { label: 'Target Date',  value: drawer.target_date || '—' },
                  { label: 'Planned Hrs',  value: drawer.planned_hours ? `${drawer.planned_hours}h` : '—' },
                ].map(item => (
                  <div key={item.label} style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px 12px' }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>{item.label}</div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{item.value}</div>
                  </div>
                ))}
              </div>

              {/* Description */}
              {drawer.description && (
                <div>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '6px' }}>Description</div>
                  <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.6', background: '#f8fafc', borderRadius: '8px', padding: '12px' }}>{drawer.description}</div>
                </div>
              )}

              {/* Impact */}
              {drawer.impact && (
                <div>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '6px' }}>Impact</div>
                  <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.6', background: '#fef3c7', borderRadius: '8px', padding: '12px', border: '1px solid #fde68a' }}>{drawer.impact}</div>
                </div>
              )}

              {/* Linked tasks */}
              {drawer.linked_tasks && drawer.linked_tasks.length > 0 && (
                <div>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px' }}>Linked Tasks ({drawer.linked_tasks.length})</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {drawer.linked_tasks.map(t => (
                      <div key={t.task_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderRadius: '8px', padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{t.task_name}</span>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <span style={{ fontSize: '11px', color: '#94a3b8' }}>{t.planned_start} → {t.planned_end}</span>
                          {t.status_name && <span className="badge badge-info" style={{ fontSize: '10px' }}>{t.status_name}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: '10px', paddingTop: '8px' }}>
                <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => { setDrawer(null); openEdit(drawer); }}>✏️ Edit</button>
                <button className="btn btn-danger" onClick={() => { handleDelete(drawer.cr_id, drawer.cr_ref); setDrawer(null); }}>🗑️ Delete</button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ChangeRequests;
